import { decodeTerminalBinaryFrame, TerminalBinaryOpcode } from "@rivets/core/terminal-protocol";

export type TerminalV2Frame =
  | { type: "terminal.created"; terminalId: string; incarnation: number; state?: string }
  | { type: "terminal.attached"; terminalId: string; incarnation: number; highSeq: number; lease?: { leaseId: string; epoch: number; expiresAt: number } }
  | { type: "terminal.snapshot"; terminalId: string; incarnation: number; sequence: number; data: string; chunkIndex: number; chunkCount: number; truncatedBeforeSeq?: number }
  | { type: "terminal.data"; terminalId: string; incarnation: number; sequence: number; data: string }
  | { type: "terminal.exit"; terminalId: string; incarnation: number; code: number }
  | { type: "terminal.lost"; terminalId: string; incarnation: number; code: number }
  | { type: "terminal.error"; terminalId: string; requestId?: string; code: string; message: string }
  | { type: "terminal.closed"; terminalId: string }
  | { type: "terminal.lease"; terminalId: string; leaseId: string; epoch: number; expiresAt: number };

export type TerminalPayloadFrame = TerminalV2Frame | {
  type: "terminal.binary"; terminalId: string; incarnation: number; sequence: number; opcode: number; data: Uint8Array;
};

export function parseTerminalV2Frame(raw: string): TerminalV2Frame | undefined {
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    const terminalId = typeof value.terminalId === "string" ? value.terminalId : undefined;
    if (!terminalId || typeof value.type !== "string") return undefined;
    const incarnation = integer(value.incarnation);
    switch (value.type) {
      case "terminal.created": return incarnation === undefined ? undefined : { type: value.type, terminalId, incarnation, ...(typeof value.state === "string" ? { state: value.state } : {}) };
      case "terminal.attached": {
        const highSeq = integer(value.highSeq);
        if (incarnation === undefined || highSeq === undefined) return undefined;
        const lease = objectLease(value.lease);
        return { type: value.type, terminalId, incarnation, highSeq, ...(lease ? { lease } : {}) };
      }
      case "terminal.snapshot": {
        const sequence = integer(value.sequence);
        const chunkIndex = integer(value.chunkIndex); const chunkCount = integer(value.chunkCount);
        if (incarnation === undefined || sequence === undefined || chunkIndex === undefined || chunkCount === undefined || chunkIndex < 0 || chunkCount < 1 || chunkIndex >= chunkCount || typeof value.data !== "string") return undefined;
        const truncatedBeforeSeq = integer(value.truncatedBeforeSeq);
        return { type: value.type, terminalId, incarnation, sequence, data: value.data, chunkIndex, chunkCount, ...(truncatedBeforeSeq === undefined ? {} : { truncatedBeforeSeq }) };
      }
      case "terminal.data": {
        const sequence = integer(value.sequence);
        return incarnation === undefined || sequence === undefined || typeof value.data !== "string" ? undefined : { type: value.type, terminalId, incarnation, sequence, data: value.data };
      }
      case "terminal.exit":
      case "terminal.lost": return incarnation !== undefined && typeof value.code === "number" && Number.isInteger(value.code) ? { type: value.type, terminalId, incarnation, code: value.code } : undefined;
      case "terminal.error": return typeof value.code === "string" && typeof value.message === "string" ? { type: value.type, terminalId, code: value.code, message: value.message, ...(typeof value.requestId === "string" ? { requestId: value.requestId } : {}) } : undefined;
      case "terminal.closed": return { type: value.type, terminalId };
      case "terminal.lease": {
        const epoch = integer(value.epoch); const expiresAt = integer(value.expiresAt);
        return typeof value.leaseId === "string" && epoch !== undefined && expiresAt !== undefined ? { type: value.type, terminalId, leaseId: value.leaseId, epoch, expiresAt } : undefined;
      }
      default: return undefined;
    }
  } catch { return undefined; }
}

export function parseTerminalBinaryFrame(raw: ArrayBuffer): TerminalPayloadFrame | undefined {
  const frame = decodeTerminalBinaryFrame(raw);
  if (!frame || frame.sequence > BigInt(Number.MAX_SAFE_INTEGER)) return undefined;
  return { type: "terminal.binary", terminalId: frame.terminalId, incarnation: frame.incarnation, sequence: Number(frame.sequence), opcode: frame.opcode, data: frame.payload };
}

export function isOutputOpcode(opcode: number): boolean {
  return opcode === TerminalBinaryOpcode.output || opcode === TerminalBinaryOpcode.snapshot;
}

export function decodeBase64(value: string): Uint8Array | undefined {
  try { return Uint8Array.from(atob(value), (character) => character.charCodeAt(0)); } catch { return undefined; }
}

function integer(value: unknown): number | undefined { return typeof value === "number" && Number.isSafeInteger(value) ? value : undefined; }
function objectLease(value: unknown): { leaseId: string; epoch: number; expiresAt: number } | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Record<string, unknown>;
  const epoch = integer(candidate.epoch); const expiresAt = integer(candidate.expiresAt);
  return typeof candidate.leaseId === "string" && epoch !== undefined && expiresAt !== undefined ? { leaseId: candidate.leaseId, epoch, expiresAt } : undefined;
}
